precision highp float;

uniform float uTime;
uniform vec2  uResolution;
uniform vec2  uMouse;

varying vec2 vUv;

// Simplex noise helpers
vec3 mod289v3(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289v2(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x)  { return mod289v3(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1  = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy  -= i1;
    i = mod289v2(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h  = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x  = a0.x  * x0.x   + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
}

void main() {
    vec2 uv    = vUv;
    vec2 mouse = uMouse;

    float aspect = uResolution.x / uResolution.y;
    vec2 p  = (uv    - 0.5) * vec2(aspect, 1.0);
    vec2 mp = (mouse - 0.5) * vec2(aspect, 1.0);

    float t = uTime * 0.18;

    float mouseDist      = length(p - mp);
    float mouseInfluence = smoothstep(0.6, 0.0, mouseDist) * 0.35;

    float n1 = snoise(p * 1.2 + vec2( t * 0.7,  t * 0.4)) * 0.5 + 0.5;
    float n2 = snoise(p * 2.4 + vec2(-t * 0.5,  t * 0.8) + mp * 0.4) * 0.5 + 0.5;
    float n3 = snoise(p * 4.8 + vec2( t * 1.1, -t * 0.6)) * 0.5 + 0.5;
    float n4 = snoise(p * 0.6 + vec2(-t * 0.3, -t * 0.2)) * 0.5 + 0.5;

    float combined = n1 * 0.45 + n2 * 0.28 + n3 * 0.15 + n4 * 0.12;
    combined += mouseInfluence * (n2 * 0.5 + 0.5);

    // Brand palette — linear RGB (sRGB hex converted for correct #include <colorspace_fragment> output)
    vec3 colDark     = vec3(0.002, 0.002, 0.002);   // #080808
    vec3 colMidnight = vec3(0.003, 0.023, 0.040);   // #0A4152 dimmed
    vec3 colPlum     = vec3(0.060, 0.006, 0.004);   // #701B15 dimmed
    vec3 colCaramel  = vec3(0.171, 0.064, 0.027);   // #EDBA97 subtle glow

    vec3 col = colDark;
    col = mix(col, colMidnight, smoothstep(0.30, 0.65, combined) * 0.85);
    col = mix(col, colPlum,     smoothstep(0.55, 0.80, n2) * smoothstep(0.4, 0.7, n4) * 0.7);
    col = mix(col, colCaramel,  smoothstep(0.78, 0.95, combined) * 0.18);

    // Vignette
    float vignette = 1.0 - smoothstep(0.4, 1.2, length(p));
    col *= vignette * 0.6 + 0.4;

    // Edge darkening
    col *= 0.85 + 0.15 * (1.0 - abs(uv.x - 0.5) * 2.0) * (1.0 - abs(uv.y - 0.5) * 2.0);

    gl_FragColor = vec4(col, 1.0);
    #include <colorspace_fragment>
}
